import React, { useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import saveFill from "@iconify/icons-eva/save-outline";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { LoadingButton } from "@material-ui/lab";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { Alert, AlertTitle } from "@material-ui/lab";
import LockIcon from "@material-ui/icons/Lock";
import { useSelector } from "react-redux";

import { clientesApi } from "src/services/clientesApi";
import { useSnackbar } from "src/components/snackbar";

export default function EditarClavesPortales(props) {
  const { open, setOpen, refCliente, elementoSeleccionado } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [editarClave] = clientesApi.useEditarClaveClienteMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Obtener sucursal activa para auditoría
  const {
    sucursal: { refSucursal },
  } = useSelector((state) => state.auth.user);

  /**
   * Se encarga de cerrar el modal de agregar empleado
   */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  /**
   * Se encarga de hacer las verificaciones para los distinso campos del
   * formulario de agregar empleados.
   */
  const LoginSchema = Yup.object().shape({
    clave: Yup.string().required("Se debe ingresar una clave"),
  });

  /**
   * Objeto encargado de validar los valores ingresados al formulario, según las
   * validaciones entregadas por LoginSchema.
   */
  const formik = useFormik({
    initialValues: {
      clave: elementoSeleccionado.clave,
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      guardarDatos();
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  /**
   * Se encargar de guardar los datos ingresados por el usuario, ya validados
   * a la variable empleado, que es la que se guardará en la bd.
   */
  const guardarDatos = useCallback(async () => {
    const body = {
      refCliente,
      clave: getFieldProps("clave").value,
      institucion: elementoSeleccionado.institucion,
      nombreUsuario: elementoSeleccionado.usuario,
      refSucursal,
    };

    try {
      await editarClave(body).unwrap();
      enqueueSnackbar("Clave editada correctamente", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      handleClose();
    } catch (error) {
      console.error("Error al editar la contraseña: ", error);
      enqueueSnackbar(
        error?.data?.message ||
          error.message ||
          "Ocurrió un error desconocido al editar la clave",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    }
  }, [
    editarClave,
    elementoSeleccionado.institucion,
    elementoSeleccionado.usuario,
    enqueueSnackbar,
    getFieldProps,
    handleClose,
    refCliente,
    refSucursal,
  ]);

  const handleShowPassword = () => {
    setShowPassword((showPassword) => !showPassword);
  };

  return (
    <>
      <div>
        <Dialog open={open} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">
            Editar Claves Cliente
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ingrese nueva contraseña del cliente.
            </DialogContentText>
            <Alert
              severity="info"
              icon={<LockIcon />}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <AlertTitle>Re-encriptación Segura</AlertTitle>
              La nueva contraseña será encriptada automáticamente con{" "}
              <strong>AES-256-GCM</strong> (formato más seguro) al guardar.
            </Alert>

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <TextField
                  disabled={true}
                  margin="dense"
                  label="Nombre de Usuario"
                  fullWidth
                  value={elementoSeleccionado.usuario}
                />

                <FormControl margin="dense" fullWidth disabled={true}>
                  <InputLabel id="demo-simple-select-label">
                    Tipo Cliente
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    value={elementoSeleccionado.tipoClave}
                    label="Tipo Cliente"
                  >
                    <MenuItem value={"SOCIO"}>SOCIO</MenuItem>
                    <MenuItem value={"REPRESENTANTE LEGAL"}>
                      REPRESENTANTE LEGAL
                    </MenuItem>
                    <MenuItem value={"EMPRESA"}>EMPRESA</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  disabled={true}
                  margin="dense"
                  label="Institucion"
                  fullWidth
                  value={elementoSeleccionado.institucion}
                />

                <TextField
                  margin="dense"
                  required
                  fullWidth
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  label="Clave del cliente"
                  {...getFieldProps("clave")}
                  style={{ marginBottom: 15 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.clave && errors.clave)}
                  helperText={touched.clave && errors.clave}
                />

                <DialogActions>
                  <LoadingButton onClick={handleClose} color="error">
                    Cancelar
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    color="primary"
                    startIcon={<Icon icon={saveFill} />}
                    loading={isSubmitting}
                  >
                    Guardar Claves Cliente
                  </LoadingButton>
                </DialogActions>
              </Form>
            </FormikProvider>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
